'use client';

import type { Tabs as TabsPrimitive } from 'radix-ui';
import {
  type ComponentProps,
  createContext,
  type ElementType,
  type ReactNode,
  useContext,
} from 'react';

import { tv } from '../../lib/tv';
import { Badge } from '../ui/badge';
import {
  Tabs as TabsRoot,
  TabsContent,
  TabsList as TabsListPrimitive,
  TabsTrigger,
} from '../ui/tabs';

type TabVariant = 'underline' | 'pill';

/**
 * Figma Tab (75:78) and Tab List (75:154), page ↳ Tabs.
 *
 * Tab: 12px sides, gap space/1_5, Label/Medium (SemiBold in every state so the
 * width never shifts), optional 16px icon and a Small Subtle Badge count.
 * - Underline: 44 tall. Default text/secondary; Hover text/primary + a 2px
 *   border/strong indicator; Active text/primary + brand/strong indicator;
 *   Disabled text/disabled. Indicator radius 2 on the bottom edge.
 * - Pill: 32 tall, radius/md. Hover bg/emphasis; Active bg/surface + Shadow/XS.
 *   Counts sit on bg/surface (Active: Brand badge).
 * - Focus: 2px border/focus inside stroke (radius/sm on Underline).
 *
 * Tab List: Underline sits on a full-width 1px border/subtle rule, tabs 4px
 * apart. Pill: bg/muted track, radius/lg, 4px padding, 2px gap.
 */
export const tabStyles = tv({
  slots: {
    list: 'flex items-center',
    tab: [
      'group/tab relative inline-flex shrink-0 cursor-pointer items-center justify-center gap-1.5 px-3',
      'text-label-md text-text-secondary whitespace-nowrap outline-none',
      'transition-[color,background-color,box-shadow] duration-[120ms] ease-out',
      'hover:text-text-primary data-[state=active]:text-text-primary',
      'focus-visible:inset-ring-2 focus-visible:inset-ring-border-focus',
      'disabled:pointer-events-none disabled:cursor-default disabled:text-text-disabled',
    ],
    icon: [
      'size-4 shrink-0 text-icon-secondary transition-colors duration-[120ms] ease-out',
      'group-hover/tab:text-icon-primary group-data-[state=active]/tab:text-icon-primary',
      'group-disabled/tab:text-text-disabled',
    ],
    // Neutral Subtle Badge; the Active tab's count is the Brand one. Group
    // variants out-rank the Badge's own tone classes.
    count:
      'group-data-[state=active]/tab:bg-brand-subtle group-data-[state=active]/tab:text-text-brand',
    indicator: '',
  },
  variants: {
    variant: {
      underline: {
        list: 'w-full gap-1 border-b border-border-subtle',
        tab: 'h-11 rounded-sm',
        indicator: [
          'pointer-events-none absolute inset-x-0 bottom-0 h-0.5 rounded-[2px] bg-transparent',
          'transition-colors duration-[120ms] ease-out',
          'group-hover/tab:bg-border-strong group-data-[state=active]/tab:bg-brand-strong',
        ],
      },
      pill: {
        list: 'inline-flex w-fit gap-0.5 rounded-lg bg-bg-muted p-1',
        tab: [
          'h-8 rounded-md',
          'data-[state=inactive]:hover:bg-bg-emphasis',
          'data-[state=active]:bg-bg-surface data-[state=active]:shadow-xs',
        ],
        count: 'group-data-[state=inactive]/tab:bg-bg-surface',
        indicator: 'hidden',
      },
    },
  },
  defaultVariants: { variant: 'underline' },
});

const TabVariantContext = createContext<TabVariant>('underline');

export type TabsProps = ComponentProps<typeof TabsPrimitive.Root>;

/**
 * Switch between views of the same object without leaving the page. Radix
 * Tabs: `value` / `defaultValue` / `onValueChange`; arrow keys move between
 * tabs (Home / End jump), and the focused tab is selected
 * (`activationMode="manual"` makes Enter / Space select instead).
 */
export function Tabs(props: TabsProps) {
  return <TabsRoot {...props} />;
}

export type TabListProps = Omit<ComponentProps<typeof TabsPrimitive.List>, 'children'> & {
  /**
   * Figma "Style". Underline for page-level sections; Pill as a compact
   * segmented control for 2–5 options inside cards and toolbars.
   */
  variant?: TabVariant;
  /** Names the tab list when there is no visible heading, e.g. "Reel sections". */
  'aria-label'?: string;
  children: ReactNode;
};

/** Figma Tab List: a row of Tabs, exactly one Active. */
export function TabList({ variant = 'underline', className, ...props }: TabListProps) {
  return (
    <TabVariantContext.Provider value={variant}>
      <TabsListPrimitive
        data-variant={variant}
        className={tabStyles({ variant }).list({ className })}
        {...props}
      />
    </TabVariantContext.Provider>
  );
}

export type TabProps = Omit<
  ComponentProps<typeof TabsPrimitive.Trigger>,
  'children' | 'asChild'
> & {
  /** The panel this tab shows. */
  value: string;
  /** Figma "Label". */
  children: ReactNode;
  /** Figma "Icon" - a 16px icon before the label. */
  icon?: ElementType<{ className?: string }>;
  /** Figma "Count" - a Small Subtle Badge after the label (Brand when active). */
  count?: ReactNode;
};

/** One tab (Figma Tab). Its style comes from the surrounding `TabList`. */
export function Tab({
  icon: Icon,
  count,
  disabled,
  children,
  className,
  ...props
}: TabProps) {
  const variant = useContext(TabVariantContext);
  const styles = tabStyles({ variant });
  return (
    <TabsTrigger
      data-variant={variant}
      disabled={disabled}
      className={styles.tab({ className })}
      {...props}
    >
      {Icon && <Icon aria-hidden className={styles.icon()} />}
      {/* data-disabled on the text itself: axe skips disabled text contrast. */}
      <span data-slot="tab-label" data-disabled={disabled ? '' : undefined}>
        {children}
      </span>
      {count !== undefined && count !== null && (
        <Badge data-slot="tab-count" tone="neutral" className={styles.count()}>
          {count}
        </Badge>
      )}
      <span aria-hidden data-slot="tab-indicator" className={styles.indicator()} />
    </TabsTrigger>
  );
}

export type TabPanelProps = ComponentProps<typeof TabsPrimitive.Content>;

/** The content a Tab shows (`role="tabpanel"`, labelled by its tab). */
export function TabPanel(props: TabPanelProps) {
  return <TabsContent {...props} />;
}
